/**
 * Core Charge auto-add + sync module
 * -----------------------------------
 * Theme-only (Ajax Cart API), no Shopify Functions / app required.
 *
 * Data model:
 * - product metafield custom.core_charge_amount (Money or Decimal) sets the
 *   core amount; product.liquid renders it into #core-charge-data (in cents).
 * - Hidden product "core-charge" has one variant per amount ($25 steps).
 * - The part line and its core line share a `_core_charge_pair` property.
 *   Part line also stores `_core_variant_id` so a missing core line can be
 *   re-added. Core line stores `_core_charge_for` (part handle).
 *
 * Rules enforced on every page load / cart change:
 * - every part line has a core line at the same quantity (re-added / synced)
 * - core lines with no part line are removed (part removed => core removed)
 * - core lines added on their own (no pair) are removed
 */
(function () {
  const CORE_CHARGE_PRODUCT_HANDLE = "core-charge";
  const RECONCILE_DEBOUNCE_MS = 300;

  let coreVariantsCache = null;

  async function getCoreChargeVariants() {
    if (coreVariantsCache) return coreVariantsCache;
    const res = await fetch(`/products/${CORE_CHARGE_PRODUCT_HANDLE}.js`);
    if (!res.ok) throw new Error("Core Charge product not found - check handle / Online Store channel");
    const product = await res.json();
    coreVariantsCache = product.variants.map(v => ({ id: v.id, price: v.price, available: v.available }));
    return coreVariantsCache;
  }

  async function findCoreVariant(amountCents) {
    const variants = await getCoreChargeVariants();
    const amount = Number(amountCents);
    const exact = variants.find(v => v.price === amount);
    if (exact) return exact;
    // Foreign currencies: Shopify converts the metafield and the variant price
    // separately, so they can round a little differently. Take the closest
    // variant, but only within 3% so a wrong amount never picks a wrong core.
    let closest = null;
    for (const v of variants) {
      if (!closest || Math.abs(v.price - amount) < Math.abs(closest.price - amount)) closest = v;
    }
    if (closest && Math.abs(closest.price - amount) <= amount * 0.03) return closest;
    console.error(`[CoreCharge] No Core Charge variant priced at ${amount / 100}. Add one to the "${CORE_CHARGE_PRODUCT_HANDLE}" product.`);
    return null;
  }

  function isCoreLine(item) {
    return item.handle === CORE_CHARGE_PRODUCT_HANDLE || !!(item.properties && item.properties._core_charge_for);
  }

  function notifyCartUpdated(cart) {
    document.dispatchEvent(new CustomEvent("core-charge:cart-updated", { detail: cart }));
  }

  /**
   * Adds the core charge line, then the part (submitted as the real form data,
   * so options/uploads from other apps are kept).
   */
  async function addWithCoreCharge(form, coreData) {
    const coreVariant = await findCoreVariant(coreData.amountCents);
    if (!coreVariant) throw new Error("Core charge variant missing");

    const formData = new FormData(form);
    const variantId = formData.get("id");
    // Deterministic per variant so re-adding the same part merges lines.
    const pairId = `${coreData.productHandle}:${variantId}:${coreVariant.id}`;
    const quantity = parseInt(formData.get("quantity"), 10) || 1;
    formData.set("quantity", quantity);
    formData.set("properties[_core_charge_pair]", pairId);
    formData.set("properties[_core_variant_id]", coreVariant.id);

    // Shopify puts the most recently added line first, and checkout, order
    // pages and emails follow that order. Add the core line first so the part
    // lands above it everywhere.
    const coreRes = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{
          id: coreVariant.id,
          quantity,
          properties: { _core_charge_pair: pairId, _core_charge_for: coreData.productHandle },
        }],
      }),
    });
    if (!coreRes.ok) throw new Error("Could not add the core charge for this item");
    const coreKey = (await coreRes.json()).items[0].key;

    const partRes = await fetch("/cart/add.js", {
      method: "POST",
      headers: { Accept: "application/json" },
      body: formData,
    });
    if (!partRes.ok) {
      // Never leave a core charge in the cart without its part.
      await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: coreKey, quantity: 0 }),
      });
      const err = await partRes.json().catch(() => ({}));
      throw new Error(err.description || "Could not add item to cart");
    }
  }

  /**
   * Returns true if the cart was changed.
   */
  async function reconcileCart() {
    const cart = await fetch("/cart.js").then(r => r.json());
    const parts = cart.items.filter(i => !isCoreLine(i) && i.properties && i.properties._core_charge_pair);
    const cores = cart.items.filter(isCoreLine);

    const updates = {}; // line key -> quantity
    const toAdd = [];

    // Several part lines can share a pair (same variant, different app
    // options); the core line must carry their combined quantity.
    const pairs = {};
    for (const part of parts) {
      const pairId = part.properties._core_charge_pair;
      pairs[pairId] = pairs[pairId] || { quantity: 0, part };
      pairs[pairId].quantity += part.quantity;
    }

    for (const pairId of Object.keys(pairs)) {
      const { quantity, part } = pairs[pairId];
      const pairCores = cores.filter(c => c.properties && c.properties._core_charge_pair === pairId);
      if (pairCores.length === 0) {
        const variantId = Number(part.properties._core_variant_id);
        if (variantId) {
          toAdd.push({
            id: variantId,
            quantity,
            properties: { _core_charge_pair: pairId, _core_charge_for: part.handle },
          });
        }
        continue;
      }
      if (pairCores[0].quantity !== quantity) updates[pairCores[0].key] = quantity;
      pairCores.slice(1).forEach(c => { updates[c.key] = 0; });
    }

    for (const core of cores) {
      const pairId = core.properties && core.properties._core_charge_pair;
      if (!pairId || !pairs[pairId]) updates[core.key] = 0;
    }

    let changed = false;
    if (Object.keys(updates).length) {
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });
      changed = true;
    }
    if (toAdd.length) {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: toAdd }),
      });
      if (res.ok) changed = true;
      else console.error("[CoreCharge] could not re-add core charge lines:", await res.text());
    }
    if (changed) notifyCartUpdated(await fetch("/cart.js").then(r => r.json()));
    return changed;
  }

  let reconcileTimer = null;
  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(async () => {
      try {
        const changed = await reconcileCart();
        // The cart page is server-rendered, so reload to show the fixed cart.
        if (changed && isCartPage() && !reloadedRecently()) window.location.reload();
      } catch (err) {
        console.error("[CoreCharge] reconcile failed:", err);
      }
    }, RECONCILE_DEBOUNCE_MS);
  }

  // Guards against a reload loop if the cart can't be fixed (e.g. core variant out of stock).
  function reloadedRecently() {
    try {
      const last = Number(sessionStorage.getItem("coreChargeReloadAt")) || 0;
      if (Date.now() - last < 10000) return true;
      sessionStorage.setItem("coreChargeReloadAt", String(Date.now()));
    } catch (e) {}
    return false;
  }

  function isCartPage() {
    return window.location.pathname.replace(/\/$/, "").endsWith("/cart");
  }

  /** Product page: intercept add-to-cart only for products with a core charge. */
  function bindProductForm() {
    const form = document.getElementById("addToCartForm");
    const dataEl = document.getElementById("core-charge-data");
    if (!form || !dataEl) return;
    const coreData = JSON.parse(dataEl.textContent);

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      const submitBtn = document.getElementById("addToCart");
      const originalLabel = submitBtn ? submitBtn.value : null;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.value = "Adding...";
      }
      try {
        await addWithCoreCharge(form, coreData);
        window.location.href = "/cart";
      } catch (err) {
        console.error("[CoreCharge] add-to-cart failed:", err);
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.value = originalLabel;
        }
        alert(err.message || "Something went wrong adding this item. Please try again.");
      }
    }, true);
  }

  /** Cart page: keep core qty tied to its part, remove both together. */
  function bindCartPage() {
    const rows = document.querySelectorAll('[data-core-role="parent"]');
    if (!rows.length) return;

    const syncCoreInputs = () => {
      const totals = {};
      rows.forEach(row => {
        const partInput = row.querySelector('input[name^="updates["]');
        const pairId = row.getAttribute("data-core-pair");
        totals[pairId] = (totals[pairId] || 0) + Math.max(0, parseInt(partInput.value, 10) || 0);
      });
      document.querySelectorAll(".core-charge-qty").forEach(coreInput => {
        const qty = totals[coreInput.getAttribute("data-core-pair")];
        if (qty === undefined) return;
        coreInput.value = qty;
        const display = coreInput.parentNode.querySelector(".core-charge-qty-display");
        if (display) display.textContent = qty;
      });
    };

    const isQtyInput = el => el.name && el.name.indexOf("updates[") === 0 && !el.classList.contains("core-charge-qty");
    document.addEventListener("input", e => { if (isQtyInput(e.target)) syncCoreInputs(); });
    document.addEventListener("change", e => { if (isQtyInput(e.target)) syncCoreInputs(); });
    // Theme +/- buttons set the value without firing events.
    document.addEventListener("click", e => {
      if (e.target.closest(".plus_btn, .minus_btn")) setTimeout(syncCoreInputs, 0);
    });
    document.querySelectorAll('form[action="/cart"]').forEach(f => f.addEventListener("submit", syncCoreInputs, true));

    rows.forEach(row => {
      const del = row.querySelector(".del_btn");
      if (!del) return;
      del.addEventListener("click", async e => {
        e.preventDefault();
        e.stopImmediatePropagation();
        const pairId = row.getAttribute("data-core-pair");
        const updates = {};
        document.querySelectorAll("[data-core-pair][data-key]").forEach(el => {
          if (el.getAttribute("data-core-pair") === pairId) updates[el.getAttribute("data-key")] = 0;
        });
        await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ updates }),
        });
        window.location.reload();
      }, true);
    });
  }

  /** "How it works" links open the explainer modal; href stays as a fallback. */
  function bindModal() {
    const modal = document.getElementById("core-charge-modal");
    if (!modal || typeof modal.showModal !== "function") return;
    document.addEventListener("click", e => {
      if (e.target.closest("[data-core-charge-modal]")) {
        e.preventDefault();
        modal.showModal();
      } else if (e.target.closest("[data-core-charge-modal-close]") || e.target === modal) {
        modal.close(); // clicking the backdrop targets the dialog itself
      }
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    bindModal();
    bindProductForm();
    bindCartPage();
    scheduleReconcile();
  });

  window.CoreCharge = { addWithCoreCharge, reconcileCart };
})();
